import {useState, useRef, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormGroup, Checkbox, IconButton, Popover, InputAdornment, useTheme} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import {Virtuoso} from 'react-virtuoso'
import {
  Container,
  StyledOutlinedInput,
  StyledFormControlLabel,
  StyledChip,
  StyledChipContainer,
  PopoverContent,
  StyledCloseIcon,
  StyledSearchInput,
  NoResultsMessage,
  getPopoverPaperStyles
} from './CheckboxGroupStyles'

/**
 * CheckboxGroup - Independent checkbox selection component
 *
 * A standalone, reusable checkbox group with popover, select-all, and chips display.
 * Can be used anywhere in your application without FilterHeader dependency.
 *
 * @param {Object} props
 * @param {Array} props.options - Array of option objects with {label, value} structure
 * @param {Array} props.selectedValues - Currently selected values
 * @param {Function} props.onChange - Callback when selection changes (values: Array)
 * @param {string} props.placeholder - Placeholder text for input field
 * @param {boolean} props.showChips - Whether to show selected chips below input (default: true)
 * @param {boolean} props.showSelectAll - Whether to show "Select All" option (default: true)
 * @param {string} props.selectAllLabel - Label for select all option (default: "Select All")
 * @param {number} props.maxHeight - Max height for popover list (default: '70vh')
 * @param {string} props.ariaLabel - Aria label for accessibility
 */
const CheckboxGroup = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Select options',
  showChips = true,
  showSelectAll = true,
  selectAllLabel = 'Select All',
  maxHeight = '70vh',
  ariaLabel = 'Checkbox group',
  ...rest
}) => {
  const theme = useTheme()
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [localSelectedValues, setLocalSelectedValues] = useState(selectedValues)
  const [searchQuery, setSearchQuery] = useState('')

  // Update local state when external selectedValues change
  useEffect(() => {
    setLocalSelectedValues(selectedValues)
  }, [selectedValues])

  const togglePopover = () => {
    setOpen(!open)
    // Clear search when closing popover
    if (open) {
      setSearchQuery('')
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      togglePopover()
    }
  }

  const handleCheckboxChange = (value) => {
    let newValues
    if (localSelectedValues.includes(value)) {
      newValues = localSelectedValues.filter((v) => v !== value)
    } else {
      newValues = [...localSelectedValues, value]
    }
    setLocalSelectedValues(newValues)
    if (onChange) {
      onChange(newValues)
    }
  }

  const handleSelectAll = (event) => {
    let newValues
    if (event.target.checked) {
      newValues = options.map((opt) => opt.value)
    } else {
      newValues = []
    }
    setLocalSelectedValues(newValues)
    if (onChange) {
      onChange(newValues)
    }
  }

  const handleChipDelete = (value) => {
    const newValues = localSelectedValues.filter((v) => v !== value)
    setLocalSelectedValues(newValues)
    if (onChange) {
      onChange(newValues)
    }
  }

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options
    const lowerQuery = searchQuery.toLowerCase()
    return options.filter((option) => option.label.toLowerCase().includes(lowerQuery))
  }, [options, searchQuery])

  const selectAllChecked = filteredOptions.length > 0 && localSelectedValues.length === options.length
  const selectAllIndeterminate = localSelectedValues.length > 0 && localSelectedValues.length < options.length

  // Performance optimization: Create a Set for O(1) lookup of selected values
  const selectedValuesSet = useMemo(() => new Set(localSelectedValues), [localSelectedValues])

  // Performance optimization: Create a Map of value -> label for chips
  const selectedOptions = useMemo(() => {
    const valueMap = new Map(options.map((opt) => [opt.value, opt.label]))
    return localSelectedValues
      .map((value) => ({
        value,
        label: valueMap.get(value) || value
      }))
      .filter((opt) => valueMap.has(opt.value))
  }, [options, localSelectedValues])

  // Determine if we should use virtualization (threshold: 100 items)
  const useVirtualization = options.length > 100
  const showSearchBox = options.length > 100
  const ITEM_HEIGHT = 42 // Height of each checkbox item in pixels
  const MAX_VISIBLE_ITEMS = 10 // Show up to 10 items before scrolling
  const listHeight = Math.min(filteredOptions.length * ITEM_HEIGHT, MAX_VISIBLE_ITEMS * ITEM_HEIGHT)

  // Handlers for search
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value)
  }

  const handleSearchClear = () => {
    setSearchQuery('')
  }

  // Row renderer for virtualized list
  const renderCheckboxItem = (index) => {
    const option = filteredOptions[index]
    const isChecked = selectedValuesSet.has(option.value)
    return (
      <StyledFormControlLabel
        control={
          <Checkbox checked={isChecked} onChange={() => handleCheckboxChange(option.value)} value={option.value} />
        }
        label={option.label}
      />
    )
  }

  return (
    <Container {...rest}>
      <StyledOutlinedInput
        fullWidth
        readOnly
        ref={anchorRef}
        placeholder={placeholder || `(${options.length} Values)`}
        size='small'
        endAdornment={<InputAdornment position='end'>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</InputAdornment>}
        onClick={togglePopover}
        onKeyDown={handleKeyDown}
        inputProps={{
          'aria-label': ariaLabel,
          'aria-expanded': open,
          'aria-haspopup': 'listbox'
        }}
      />

      {showChips && localSelectedValues.length > 0 && (
        <StyledChipContainer>
          {selectedOptions.map((option) => (
            <StyledChip
              key={option.value}
              label={option.label}
              title={option.label}
              onDelete={() => handleChipDelete(option.value)}
              deleteIcon={
                <IconButton size='small'>
                  <StyledCloseIcon />
                </IconButton>
              }
              aria-label={`${option.label}, selected. Press delete to remove`}
            />
          ))}
        </StyledChipContainer>
      )}

      <Popover
        elevation={0}
        slotProps={{
          paper: {
            sx: getPopoverPaperStyles(anchorRef.current?.clientWidth, maxHeight, theme)
          }
        }}
        open={open}
        anchorEl={anchorRef.current}
        onClose={togglePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        aria-label={ariaLabel}
      >
        <PopoverContent>
          {/* Search Box - Only shown for large lists */}
          {showSearchBox && (
            <StyledSearchInput
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={`Search ${options.length} options...`}
              size='small'
              startAdornment={
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              }
              endAdornment={
                searchQuery && (
                  <InputAdornment position='end'>
                    <IconButton size='small' onClick={handleSearchClear} edge='end' aria-label='Clear search'>
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                )
              }
              inputProps={{
                'aria-label': 'Search options'
              }}
            />
          )}

          <FormGroup>
            {showSelectAll && !searchQuery && (
              <StyledFormControlLabel
                control={
                  <Checkbox
                    checked={selectAllChecked}
                    indeterminate={selectAllIndeterminate}
                    onChange={handleSelectAll}
                    value='selectAll'
                  />
                }
                label={selectAllLabel}
              />
            )}

            {/* No Results Message */}
            {filteredOptions.length === 0 && searchQuery && (
              <NoResultsMessage>No results found for &quot;{searchQuery}&quot;</NoResultsMessage>
            )}

            {/* Individual Options - Virtualized or Regular based on item count */}
            {filteredOptions.length > 0 &&
              (useVirtualization ? (
                <Virtuoso
                  style={{height: listHeight}}
                  totalCount={filteredOptions.length}
                  itemContent={renderCheckboxItem}
                  overscan={200}
                />
              ) : (
                filteredOptions.map((option) => {
                  const isChecked = selectedValuesSet.has(option.value)
                  return (
                    <StyledFormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(option.value)}
                          value={option.value}
                        />
                      }
                      label={option.label}
                    />
                  )
                })
              ))}
          </FormGroup>
        </PopoverContent>
      </Popover>
    </Container>
  )
}

CheckboxGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  selectedValues: PropTypes.array,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  showChips: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  selectAllLabel: PropTypes.string,
  maxHeight: PropTypes.string,
  ariaLabel: PropTypes.string
}

export default CheckboxGroup
